import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import '../styles/CompanyDetails.css';

export default function CompanyDetails() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);

  useEffect(() => {
    loadCompanyData();
  }, [companyId]);

  const loadCompanyData = async () => {
    try {
      const [companyRes, usersRes] = await Promise.all([
        fetch(`/api/companies/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`/api/users/company/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      const company = await companyRes.json();
      const usersData = await usersRes.json();

      setData(company);
      setUsers(usersData.users || []);
      setFormData({
        name: company.company?.name,
        website: company.company?.website,
        subscription_status: company.company?.subscription_status
      });
    } catch (err) {
      setError('Failed to load company data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/companies/${companyId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Failed to update');
      await loadCompanyData();
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update company');
    }
  };

  const handleToggleWidget = async (enabling) => {
    const companyName = data?.company?.name || '';
    const msg = enabling
      ? `Enable web widget for ${companyName}?\n\nThis generates a unique API key.`
      : `Disable web widget for ${companyName}?\n\nTheir embed code will stop working immediately.`;
    if (!window.confirm(msg)) return;
    setWidgetLoading(true);
    try {
      const response = await fetch(`/api/owner/companies/${companyId}/widget`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: enabling }),
      });
      if (response.ok) {
        await loadCompanyData();
      } else {
        const d = await response.json().catch(() => ({}));
        setError(d.error || 'Failed to update widget');
      }
    } catch {
      setError('Failed to update widget');
    } finally {
      setWidgetLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="loading">Company not found</div>;

  const company = data.company;
  const stats = data.stats;
  const safeNum = (val) => parseFloat(val) || 0;

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
      <div className="company-details">
      <header className="header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={() => navigate('/owner/companies')} className="back-btn" style={{ marginRight: '8px' }}>← Back</button>
        <h1 style={{ margin: 0 }}>{company.name}</h1>
      </header>

        <div className="details-container">
          <section className="info-section">
            <div className="section-header">
              <h2>Company Information</h2>
              <button onClick={() => setEditing(!editing)} className="edit-btn">
                {editing ? '✕ Cancel' : '✎ Edit'}
              </button>
            </div>

            {editing ? (
              <form onSubmit={handleUpdateCompany} className="edit-form">
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input type="url" name="website" value={formData.website || ''} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="subscription_status" value={formData.subscription_status} onChange={handleFormChange}>
                    <option value="trialing">Trialing</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="complimentary">Complimentary (free)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Web Widget</label>
                  <button
                    type="button"
                    onClick={() => handleToggleWidget(!company.widget_enabled)}
                    disabled={widgetLoading}
                    style={{
                      padding: '7px 16px',
                      background: company.widget_enabled ? '#10b981' : '#e5e7eb',
                      color: company.widget_enabled ? '#fff' : '#374151',
                      border: 'none', borderRadius: '4px', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 600,
                    }}
                  >
                    {widgetLoading ? '…' : (company.widget_enabled ? 'Enabled — click to disable' : 'Disabled — click to enable')}
                  </button>
                </div>
                <button type="submit" className="save-btn">Save Changes</button>
              </form>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <label>Name</label>
                  <value>{company.name}</value>
                </div>
                <div className="info-item">
                  <label>Website</label>
                  <value>{company.website || 'N/A'}</value>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <value className={`status ${company.subscription_status}`}>{company.subscription_status}</value>
                </div>
                <div className="info-item">
                  <label>Web Widget</label>
                  <value>
                    <button
                      onClick={() => handleToggleWidget(!company.widget_enabled)}
                      disabled={widgetLoading}
                      style={{
                        padding: '4px 14px',
                        background: company.widget_enabled ? '#10b981' : '#e5e7eb',
                        color: company.widget_enabled ? '#fff' : '#374151',
                        border: 'none', borderRadius: '4px', cursor: 'pointer',
                        fontSize: '13px', fontWeight: 600,
                      }}
                      title={company.widget_enabled ? `Enabled · API key: ${company.widget_api_key}` : 'Click to enable'}
                    >
                      {widgetLoading ? '…' : (company.widget_enabled ? 'Enabled' : 'Disabled')}
                    </button>
                  </value>
                </div>
              </div>
            )}
          </section>

          <section className="stats-section">
            <h2>Performance Stats</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Deals</div>
                <div className="stat-value">{stats.totalDeals}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Completed Deals</div>
                <div className="stat-value">{stats.completedDeals}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Deals in Pipeline</div>
                <div className="stat-value">{stats.pipelineDeals}</div>
              </div>
              <div className="stat-card" title="Lender proc fees + client fees on completed deals">
                <div className="stat-label">Revenue (Completed)</div>
                <div className="stat-value">£{safeNum(stats.completedRevenue).toFixed(2)}</div>
              </div>
              <div className="stat-card" title="Potential lender proc fees + client fees on open deals">
                <div className="stat-label">Revenue (Pipeline)</div>
                <div className="stat-value">£{safeNum(stats.pipelineRevenue).toFixed(2)}</div>
              </div>
              <div className="stat-card" title="Commission / fixed fees actually paid to introducers in the last 12 months">
                <div className="stat-label">Introducer Fees Paid (12m)</div>
                <div className="stat-value">£{safeNum(stats.introducerPaid12m).toFixed(2)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Introducers</div>
                <div className="stat-value">{stats.totalIntroducers}</div>
              </div>
            </div>
          </section>

          <section className="users-section">
            <div className="section-header">
              <h2>Users ({users.filter(u => u.status !== 'archived').length})</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {users.some(u => u.status === 'archived') && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={showArchived}
                      onChange={e => setShowArchived(e.target.checked)}
                    />
                    Show archived ({users.filter(u => u.status === 'archived').length})
                  </label>
                )}
                <button onClick={() => navigate(`/users/${companyId}`)} className="manage-btn">Manage Users</button>
              </div>
            </div>
            <div className="users-table">
              <div className="table-header">
                <div className="col-name">Name</div>
                <div className="col-email">Email</div>
                <div className="col-role">Role</div>
                <div className="col-status">Status</div>
              </div>
              {users.filter(u => showArchived || u.status !== 'archived').map((user) => (
                <div key={user.id} className="table-row">
                  <div className="col-name">{user.name}</div>
                  <div className="col-email">{user.email}</div>
                  <div className="col-role">{user.role}</div>
                  <div className="col-status">{user.status}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      </main>
    </div>
  );
}
